/*
 * The ?workOrder=<id> parameter a notification mail links to.
 *
 * There is no route for a single work order: /cmms/work-orders is the only CMMS work order page
 * and the details drawer is component state. Rather than invent a route the rest of the app would
 * then have to navigate through, the mail links to the list with the order named in the query, and
 * the page opens the drawer on it.
 *
 * Pure and on its own, in the shape of workOrderSearch.js, because the value arrives from outside
 * the app entirely — an address bar, a mail client that mangled the link, someone's bookmark — and
 * the guard against that is worth testing without rendering a page.
 */
export const WORK_ORDER_PARAM = "workOrder";

/*
 * The in-app link to a work order, in the same shape WorkOrderMailService puts in a mail, so a
 * link from inside the app and one from an inbox land on the same drawer.
 *
 * A path rather than a click handler: whatever renders it can hand it to a router Link, which
 * makes it a real anchor — ctrl-click, middle-click and "copy link" all work.
 */
export const buildWorkOrderDeepLink = (id) =>
  `/cmms/work-orders?${new URLSearchParams({ [WORK_ORDER_PARAM]: String(id) })}`;

/*
 * The work order the URL names, or null.
 *
 * Number() is deliberately not enough on its own: it reads "" and null as 0, "1.5" as a number
 * that is not an id, and "1e3" as 1000. Anything that is not a positive whole number is treated
 * as nothing at all rather than as a lookup that will 404 — the drawer is better left shut than
 * opened on an error.
 */
export const parseWorkOrderIdParam = (searchParams) => {
  const raw = searchParams?.get?.(WORK_ORDER_PARAM);
  if (raw == null || raw.trim() === "") return null;

  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;

  return parsed;
};
